/**
 * UpSurv 2.0 — the offline store (brief §3: "a surveyor in a basement with zero
 * signal completes an entire survey, including photos").
 *
 * Dexie 4 over IndexedDB, four object stores, and one rule that shapes all of
 * them: **nothing the surveyor does is ever held only in React state.** Every
 * answer, note and photo is committed to IndexedDB inside a transaction before
 * the call returns, and the same transaction appends the outbox row that will
 * eventually carry it to the server. A tab crash between the two is impossible;
 * there is no window where a write exists in memory but not on disk.
 *
 * ── Why four stores and not one ──────────────────────────────────────────────
 *
 *   surveys      the SurveyDocument, keyed by surveyId, plus the local sync
 *                bookkeeping (dirty flag, local revision, acked revisions).
 *   photoBlobs   the image bytes, keyed by photoId, DELIBERATELY SEPARATE.
 *                A survey with 40 photos is ~100 MB of JPEG. If the bytes lived
 *                in the document, every single answer write — every stepper tap
 *                on a machine room floor — would rewrite 100 MB. Splitting them
 *                makes a document write proportional to the document and a photo
 *                write proportional to one photo. `SurveyDocument.photos` keeps
 *                only the PhotoRef metadata, which is what types.ts already
 *                models.
 *   photoThumbs  the 640-px JPEG made at capture (round 8.1, task 3), keyed by
 *                photoId like the original and kept in its own store for the
 *                same reason: a shelf reads thumbs, a viewer reads one
 *                original, and neither read should page the other's bytes.
 *                A photo may have no thumb (a HEIC this browser cannot
 *                decode); a thumb never exists without its photo.
 *   outbox       queued mutations. One row per pending server call, keyed by a
 *                STABLE client-generated id so a replay after a lost ack lands
 *                on the same row instead of creating a second one.
 *
 * ── The outbox contract ──────────────────────────────────────────────────────
 *
 * An outbox row is removed ONLY after the server acknowledges it (see sync.ts).
 * That single rule is what makes a mid-sync disconnect safe: an interrupted
 * drain leaves the row exactly where it was, and the next drain replays it. The
 * cost of the rule is that a lost ack replays a call the server already applied,
 * which is why every row carries a stable id used as the idempotency key.
 *
 * Document rows are POINTERS, not snapshots: the drain reads the current
 * document out of `surveys` at send time. A snapshot would go stale the instant
 * the surveyor answered the next question, and reconciling a stale snapshot with
 * a live document is exactly the class of bug that loses field work.
 *
 * ── Determinism ──────────────────────────────────────────────────────────────
 *
 * No `Date.now()` and no `Math.random()` in this file. The clock is injected
 * (`SurveyStoreOptions.clock`) and every id is derived from ids the caller
 * already owns, so a test with a frozen clock produces byte-identical rows.
 */

import Dexie, { type Table } from 'dexie';

import { migrateLegacyAnswers } from '../core/legacyAnswers';
import { PhotoRefSchema } from '../core/schema';
import { isSubmittedStatus } from '../core/types';
import { photoBlobFromBytes, readPhotoBytes } from './photo-file';
import type {
  AnswerKey,
  AnswerValue,
  Note,
  PhotoRef,
  PhotoUploadState,
  SurveyDocument,
  SurveyStatus,
} from '../core/types';

/* ─────────────────────────────── clock ─────────────────────────────── */

/** Epoch milliseconds. Injected everywhere so tests can freeze time. */
export type Clock = () => number;

/**
 * The one place the real clock is read. Application code passes this in at the
 * composition root; nothing inside the store or the sync engine calls it
 * directly, so every code path under test is reproducible.
 */
export const systemClock: Clock = () => Date.now();

/** ISO-8601 instant from epoch ms — the timestamp format types.ts uses. */
export function isoAt(ms: number): string {
  return new Date(ms).toISOString();
}

/* ───────────────────────────── stored shapes ───────────────────────────── */

/**
 * A survey as the device holds it.
 *
 * `dirty`, `sealed` are 0/1 rather than booleans because IndexedDB cannot index
 * a boolean — the same reason d1-schema.sql uses INTEGER CHECK (x IN (0,1)).
 *
 * Revision bookkeeping, and what each number means:
 *
 *   localRevision   bumped on every local mutation. Monotonic per device.
 *   syncedRevision  the highest localRevision the server has acknowledged.
 *                   `dirty === (localRevision > syncedRevision)`, maintained as
 *                   a stored field so the sync pill can count dirty surveys with
 *                   an index instead of a table scan.
 *   serverRevision  the server's own revision number from the last ack.
 *                   **`serverRevision === 0` is the formal definition of "before
 *                   first successful sync"** — the precondition of the brief §4
 *                   conflict rule. See resolveConflict() in sync.ts.
 */
export interface StoredSurvey {
  surveyId: string;
  document: SurveyDocument;
  /** Denormalized from `document.status` so the list screen can index on it. */
  status: SurveyStatus;
  localRevision: number;
  syncedRevision: number;
  serverRevision: number;
  dirty: 0 | 1;
  /** 1 once the server reports the survey submitted: no further pushes. */
  sealed: 0 | 1;
  /**
   * 1 once `POST /api/surveys` has acknowledged this survey's existence — which
   * is what fires spec §2.1's "Chip and Tim are notified immediately". Only
   * field-initiated surveys ever queue that call; an assigned survey was created
   * BY the office, so there is nobody to tell.
   *
   * Stored rather than inferred so the notice is idempotent across reloads: the
   * outbox row is the promise, this flag is the receipt, and a survey whose
   * document is re-created locally cannot re-queue a notice already delivered.
   * Absent on rows written by an older build — read it as 0.
   */
  creationNotified?: 0 | 1;
  updatedAt: string;
  lastSyncedAt?: string;
}

/**
 * What this device is holding right now (D-020).
 *
 * The answer to "where did my survey go". On iOS the Home Screen app and Safari
 * are two containers with two of these, and until the app could state its own
 * counts out loud the only way to tell them apart was to read the source.
 */
export interface StorageSnapshot {
  /** Surveys on this device, submitted ones included. */
  surveys: number;
  /** Surveys that still owe the server something — dirty, or holding a row. */
  waiting: number;
  /** Outbox rows behind that number. Diagnostic detail, never the headline. */
  queuedItems: number;
  /** Photo blobs whose bytes are still on this device. */
  photos: number;
  /** Those blobs' size in bytes — what an "everything" export would carry. */
  photoBytes: number;
}

/* ────────────────────── removing a survey (D-012) ────────────────────── */

/**
 * The three ways a survey can leave a surveyor's device, which are three
 * DIFFERENT things and are kept apart everywhere — here, in the API, and in the
 * words on screen.
 *
 *   'local_delete'      The server has never heard of this survey. It exists on
 *                       this phone and nowhere else, so removing it removes it,
 *                       full stop, and there is nobody to tell.
 *   'discard'           The server knows. The document goes from this device AND
 *                       the server record is marked discarded — withdrawn from
 *                       the office, not erased from it (brief §4 is append-only;
 *                       nothing is deleted server-side either).
 *   'sealed_admin_only' Submitted AND the office holds a copy. Neither, from a
 *                       device, by anybody. The D1 schema seals a submitted
 *                       document at SQL level and the admin page is the only
 *                       route that removes one.
 */
export type RemovalPath = 'local_delete' | 'discard' | 'sealed_admin_only';

/**
 * Has the server ever learned this survey exists?
 *
 * Read off REAL STATE, never a hopeful flag, and it takes four facts because
 * any three of them alone are wrong:
 *
 *   serverRevision === 0      no document push has ever been acknowledged. On
 *                             its own this is true of a freshly ADOPTED
 *                             assignment too — the office typed an address and
 *                             the device pulled a row with no document behind
 *                             it — and deleting that locally would silently
 *                             drop a job the office is waiting on.
 *   creationNotified !== 1    `POST /api/surveys` has never been acknowledged.
 *                             That call CREATES the server's `surveys` row, so
 *                             once it lands the server knows even though no
 *                             revision exists yet.
 *   origin field_initiated    an 'assigned' survey was created BY the office;
 *                             the server has known about it since before the
 *                             phone did, whatever the two numbers above say.
 *   sealed !== 1              every writer of `sealed` — `ackSubmission`,
 *                             `markSealed`, `adoptServerDocument` — is applying
 *                             an ANSWER FROM THE SERVER. A sealed record is by
 *                             construction one the office has ruled on, so the
 *                             flag is real evidence rather than a hope, and it
 *                             is stated here explicitly instead of being left
 *                             to follow from `serverRevision` (D-018).
 */
export function neverReachedServer(record: StoredSurvey): boolean {
  return (
    record.document.origin === 'field_initiated' &&
    record.serverRevision === 0 &&
    record.creationNotified !== 1 &&
    record.sealed !== 1
  );
}

/**
 * Which of the three paths applies to this record. Pure; the UI reads it too.
 *
 * THE ORDER OF THESE TWO LINES IS THE FIX FOR THE DEAD END (D-018). It used to
 * test "submitted" first, and that produced a state with no way out: a survey
 * SUBMITTED BEFORE IT EVER SYNCED — an unlinked phone, or a basement with no
 * signal for the whole job — is sealed on the device and, because the server
 * never received it, invisible to the admin page that D-012 named as the only
 * authority allowed to remove it. Nobody could delete it: not the surveyor
 * (submitted), not the office (never arrived).
 *
 * D-012's own reasoning resolves it without an exception. True local delete is
 * granted because THE SERVER NEVER KNEW, and that fact does not stop being true
 * when the local document happens to say "submitted" — a submission that never
 * left the phone is not a record anybody else is holding. So "never reached the
 * server" is asked first, and "submitted" only decides the case where it did.
 */
export function removalPathFor(record: StoredSurvey): RemovalPath {
  if (neverReachedServer(record)) return 'local_delete';
  if (record.sealed === 1 || isSubmittedStatus(record.status)) return 'sealed_admin_only';
  return 'discard';
}

/**
 * The `photoBlobs` ROW: one photo's bytes as an `ArrayBuffer`, never a Blob
 * (schema version 2, round 8.1).
 *
 * On the design authority's iPhone the pictures did not survive a second open
 * of the app, and round 8's fetch-back from R2 did not mend it, because the
 * fetched bytes were written back as a `Blob` — the value type that had just
 * vanished. WebKit is known to lose or empty a Blob value it stored; a plain
 * `ArrayBuffer` value survives. So the row keeps the buffer, and the only Blob
 * on this table's path is the one `getPhotoBlob` builds for its caller.
 *
 * `data` is the buffer. `bytes` stays the count the PhotoRef declared, under
 * the name it has always had, because the export directory and the e2e device
 * readers read it by that name; the two are the same number for a row this
 * build wrote, and where they differ `data.byteLength` is the truth and is
 * what the storage snapshot and the transfer estimate sum.
 */
export interface PhotoBlobRow {
  photoId: string;
  surveyId: string;
  data: ArrayBuffer;
  contentType: string;
  bytes: number;
  capturedAt: string;
}

/**
 * The image bytes for one photo as `getPhotoBlob` hands them to a caller: the
 * row with its buffer rebuilt as a typed Blob, so the thumbnail hook, the
 * upload and the export keep the shape they have always read. `photoId`
 * matches `PhotoRef.id`; the metadata lives in the document and is never
 * duplicated here beyond what an upload needs without opening the document.
 */
export interface StoredPhotoBlob {
  photoId: string;
  surveyId: string;
  blob: Blob;
  contentType: string;
  bytes: number;
  capturedAt: string;
}

/**
 * The `photoThumbs` ROW: one photo's thumbnail as an `ArrayBuffer` (round
 * 8.1, task 3) — the same value type `PhotoBlobRow` keeps, for the same
 * reason. `bytes` is `data.byteLength`, kept beside it so a count never has
 * to touch the buffer. The content type is not stored: every thumb is
 * `THUMB_CONTENT_TYPE` (src/core/photoThumb.ts), whatever the original was.
 */
export interface PhotoThumbRow {
  photoId: string;
  surveyId: string;
  data: ArrayBuffer;
  bytes: number;
}

/** A version-1 row, kept only so the upgrade can name what it is reading. */
interface LegacyPhotoBlobRow {
  photoId: string;
  surveyId: string;
  blob: Blob;
  contentType: string;
  bytes: number;
  capturedAt: string;
}

/**
 * A photo's bytes read into the page's own memory as a plain `ArrayBuffer`,
 * through `readPhotoBytes` so a Blob the browser cannot read comes back as
 * ZERO bytes rather than a throw (the 2026-08-19 lesson: an unreadable
 * picture must be refused out loud, and empty is how every writer here
 * recognises it). `readPhotoBytes` is `new Uint8Array(await blob.arrayBuffer())`
 * — a fresh, whole, unshared buffer — so the view's own buffer is handed
 * on as it is; the copy the first cut made here on top of it doubled every
 * picture's footprint for the length of a write (final review's fix wave,
 * D-040 postscript). The copy survives only as the defence for a view that
 * is NOT whole or NOT a plain `ArrayBuffer`, which IndexedDB must not be
 * handed and which that reader never produces.
 */
async function photoBufferOf(blob: Blob): Promise<ArrayBuffer> {
  let bytes: Uint8Array;
  try {
    bytes = await readPhotoBytes(blob);
  } catch {
    return new ArrayBuffer(0);
  }
  const whole = bytes.buffer;
  if (whole instanceof ArrayBuffer && bytes.byteOffset === 0 && bytes.byteLength === whole.byteLength) {
    return whole;
  }
  const out = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(out).set(bytes);
  return out;
}

/**
 * Whether a `photoBlobs` row holds a picture: its `data` is an `ArrayBuffer`.
 * The one test every direct reader of the table applies, because the upgrade
 * deliberately leaves behind a record it could neither read nor delete — a
 * version-1 row with a Blob and no buffer — and such a row is no picture at
 * all: it is skipped where rows are counted and reported absent where one is
 * fetched, so the R2 fallback refills it instead of a reader crashing on it.
 */
export function hasPhotoBuffer(row: unknown): row is PhotoBlobRow {
  return (
    typeof row === 'object' &&
    row !== null &&
    (row as { data?: unknown }).data instanceof ArrayBuffer
  );
}

/** The caller-facing shape, rebuilt from a row: a typed Blob over a copy of the buffer. */
function storedPhotoBlobOf(row: PhotoBlobRow): StoredPhotoBlob {
  return {
    photoId: row.photoId,
    surveyId: row.surveyId,
    blob: photoBlobFromBytes(new Uint8Array(row.data), row.contentType),
    contentType: row.contentType,
    bytes: row.bytes,
    capturedAt: row.capturedAt,
  };
}

/** One photo's bytes as they arrive in an import file (D-020). */
export interface ImportedPhoto {
  photoId: string;
  blob: Blob;
  contentType: string;
  bytes: number;
  capturedAt: string;
}

/**
 * One survey lifted out of another device's container — the document plus the
 * sync history that decides what this device may do with it. Built by
 * `src/db/transfer.ts`, which has already validated the document against
 * `SurveyDocumentSchema`; `importSurveys` writes, it does not judge.
 */
/** A survey `importSurveys` could not write, and the error's own sentence. */
export interface ImportRefusal {
  surveyId: string;
  message: string;
}

export interface ImportResult {
  /** The surveys that landed, in file order. */
  written: StoredSurvey[];
  /** The surveys whose transaction failed; nothing of theirs is on the phone. */
  refused: ImportRefusal[];
}

export interface ImportedSurvey {
  document: SurveyDocument;
  /** The server's revision as the source device last heard it. 0 = never. */
  serverRevision: number;
  /** Whether the office is known to hold this as a submitted record. */
  sealed: boolean;
  /** Whether `POST /api/surveys` has been acknowledged for it. */
  creationNotified: boolean;
  /** When the source device last had an ack. Display only; may be absent. */
  lastSyncedAt?: string;
  /** Bytes for the photos the file actually carried. */
  photos: readonly ImportedPhoto[];
}

/**
 * `create` is its OWN kind, and that is the point of it.
 *
 * Spec §2.1: "Chip and Tim are notified immediately on creation — no job may
 * exist they don't know about." The notice is fired by `POST /api/surveys`, and
 * for a long time that call rode the document push as a side effect. A side
 * effect of another row is only as durable as that row: `ackSubmission` deletes
 * the document row, `markSealed` deletes it, and a survey created AND submitted
 * offline drains its submission having never made the creation call at all — so
 * the office never learned the job existed, silently, which is the worst way for
 * this to fail. A first-class row is deleted only by its own acknowledgement.
 *
 * `photo_thumb` (round 8.1, task 3) is the thumbnail's own row, queued behind
 * its photo's row and drained after it. Its own row for the opposite reason a
 * creation notice is: so that NOTHING about it can touch the photo. A thumb
 * that is missing, refused or parked parks only itself; the picture's row,
 * its PhotoRef and its bytes are never in the same transaction as a thumb's
 * failure.
 */
export type OutboxKind =
  | 'create'
  | 'document'
  | 'photo'
  | 'photo_thumb'
  | 'submission'
  | 'discard'
  /**
   * "Sealed on this phone; N photos still to come" (D-052 §3). Queued beside
   * the submission row and sent AHEAD of the photos, so the office can tell
   * a survey the surveyor is still working from one that is finished and on
   * its way. Carries no content; the seal itself still travels last (D-050).
   */
  | 'seal';

/**
 * `queued`    — waiting for a drain.
 * `in_flight` — handed to the transport, ack not yet seen. A row left in this
 *               state by a killed tab is recovered to `queued` on start
 *               (recoverInFlight); it is never dropped.
 * `blocked`   — the server refused THIS PAYLOAD and will refuse it again. Kept,
 *               never sent, waiting for a person.
 *
 * ── Why `blocked` exists (the 2026-08-06 incident's second defect) ───────────
 *
 * A 400 is permanent for identical bytes: the same request, replayed, produces
 * the same refusal. Before this state existed the engine retried such a row on
 * every drain trigger — start, reconnect, `visibilitychange`, the 30s backstop —
 * forever. One ad hoc photo was refused forty-two consecutive times on a real
 * device in a real building. The costs are all real and all bad: a red sync pill
 * the surveyor CANNOT clear (which teaches them to ignore the pill, which is how
 * the next real failure gets missed), and a phone spending battery and cellular
 * data on a request that cannot succeed.
 *
 * What `blocked` is NOT: it is not a delete. The row stays, the photo bytes
 * stay, the document keeps the PhotoRef. Nothing the surveyor did is discarded —
 * the row simply stops being handed to the transport until a person says to try
 * again (`retryOutboxItem`) or removes the capture. `dueOutbox()` returns only
 * `queued`, so parking is enforced by the same one-line predicate that enforces
 * the in-flight claim, and it survives a reload because it is stored.
 */
export type OutboxState = 'queued' | 'in_flight' | 'blocked';

/** What one survey still owes the office — see `outboxOwedBySurvey`. */
export interface OutboxOwed {
  /** Rows that will go on their own: queued, in flight, or under backoff. */
  sending: number;
  /** Rows parked for a person to decide about. */
  held: number;
}

export interface OutboxItem {
  /** Stable, client-generated. See `outboxId`. Also the dedupe key. */
  id: string;
  kind: OutboxKind;
  surveyId: string;
  /** Photo rows only. */
  photoId?: string;
  /**
   * The document revision this row was last (re)queued for. Documents are
   * pointers, so this is used to detect "the surveyor edited while the push was
   * in flight" at ack time rather than to reconstruct a payload.
   */
  localRevision: number;
  state: OutboxState;
  attempts: number;
  createdAt: string;
  /** Epoch ms. The row is due when `clock() >= nextAttemptAt`. */
  nextAttemptAt: number;
  lastError?: string;
}

/**
 * Stable outbox ids.
 *
 * A survey has at most ONE creation notice, ONE pending document push and ONE
 * pending submission: all three are whole-survey operations, so a second one
 * would be a duplicate, not additional work. Re-enqueueing therefore coalesces
 * onto the same row and the outbox cannot grow with keystrokes. Photos are
 * per-photo and never coalesce.
 */
export const outboxId = {
  create: (surveyId: string): string => `create:${surveyId}`,
  document: (surveyId: string): string => `doc:${surveyId}`,
  submission: (surveyId: string): string => `submit:${surveyId}`,
  seal: (surveyId: string): string => `seal:${surveyId}`,
  discard: (surveyId: string): string => `discard:${surveyId}`,
  photo: (photoId: string): string => `photo:${photoId}`,
  photoThumb: (photoId: string): string => `thumb:${photoId}`,
} as const;

/**
 * The value sent to the server as the idempotency key.
 *
 * Photos and submissions are immutable once queued, so the row id alone is
 * stable. A document push is a pointer at mutable content, so the key is stamped
 * with the revision that was actually sent: a replay of the SAME revision after
 * a lost ack is deduped, while a genuinely newer revision is a new write.
 */
export function idempotencyKeyFor(item: OutboxItem): string {
  return item.kind === 'document' ? `${item.id}@${item.localRevision}` : item.id;
}

/**
 * Drain order. Creation FIRST — spec §2.1's notice is the one message whose
 * whole value is arriving early, and it is also what puts the `surveys` row
 * behind the foreign key every later call depends on. Documents next, so the
 * server holds the content before anything references it; photos after that,
 * because a photo upload is the slowest and least reliable call in the system
 * and must never delay a document (§6.3); the SUBMISSION behind the photos
 * (D-050), because the office acts on it the moment it lands and must find
 * every picture already there.
 *
 * Order is a preference, never a dependency: every row is sent in its own
 * try/catch and none of them needs an earlier one to have succeeded.
 */
const KIND_PRIORITY: Record<OutboxKind, number> = {
  create: 0,
  document: 1,
  // A discard is the last thing this device will ever say about that survey —
  // `discardSurvey` deletes every other row for it in the same transaction, so
  // this never competes with a document or a photo for the SAME survey. It sits
  // above photos for the ordinary reason everything does: a withdrawal is one
  // small request and must not wait behind a queue of images.
  discard: 2,
  // AHEAD of the photos (D-052 §3): one small request that tells the office
  // "sealed on the phone, N pictures still to come", so the office page can
  // say so instead of *In progress* for the hours a queue can take. It says
  // nothing the seal will not say better, which is why it is one request and
  // travels first.
  seal: 3,
  photo: 4,
  // BEHIND the photos (D-050). The office builds the PDF the moment a
  // submission lands, and the design authority ruled that the office sees a
  // survey only when everything, pictures included, is there. So the
  // submission is the last thing about a survey to travel: after every photo
  // in the same pass, and — order being a preference, not a dependency —
  // `sendSubmission` also DEFERS while any photo row for the survey remains.
  submission: 5,
  // Behind the photo it belongs to, always: the office files a thumb under the
  // record's key and refuses one for a picture it does not hold yet.
  photo_thumb: 6,
};

/** Total order over the outbox: kind priority, then age, then id. Deterministic. */
export function compareOutbox(a: OutboxItem, b: OutboxItem): number {
  const byKind = KIND_PRIORITY[a.kind] - KIND_PRIORITY[b.kind];
  if (byKind !== 0) return byKind;
  if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/* ───────────────────────────── the database ───────────────────────────── */

export const DB_NAME = 'upsurv';

export class UpSurvDb extends Dexie {
  surveys!: Table<StoredSurvey, string>;
  photoBlobs!: Table<PhotoBlobRow, string>;
  photoThumbs!: Table<PhotoThumbRow, string>;
  outbox!: Table<OutboxItem, string>;

  constructor(name: string = DB_NAME) {
    super(name);
    // Version 1. Dexie index strings: first field is the primary key, the rest
    // are secondary indexes. Only what is actually queried is indexed — an index
    // on a field nobody filters by is write cost for nothing.
    this.version(1).stores({
      surveys: 'surveyId, status, dirty, updatedAt',
      photoBlobs: 'photoId, surveyId',
      outbox: 'id, surveyId, kind, state, nextAttemptAt, [surveyId+kind]',
    });
    // Version 2 (round 8.1): the same three tables, every `photoBlobs` row
    // holding its bytes as an `ArrayBuffer` instead of a Blob (`PhotoBlobRow`),
    // and one new table, `photoThumbs` (task 3), declared in the SAME bump so a
    // phone crosses from 1 to 2 once. There is no version 3.
    this.version(2)
      .stores({
        surveys: 'surveyId, status, dirty, updatedAt',
        photoBlobs: 'photoId, surveyId',
        photoThumbs: 'photoId, surveyId',
        outbox: 'id, surveyId, kind, state, nextAttemptAt, [surveyId+kind]',
      })
      .upgrade(async (tx) => {
        // Every row a version-1 build wrote is rewritten in place. A Blob that
        // reads back is kept as its buffer; one that reads empty, throws, or is
        // not a Blob at all is DELETED, because an empty row is the exact thing
        // the phone kept showing as a lost picture, and a missing row is what
        // the R2 fallback (`resolveOriginalBlob`) knows how to refill.
        //
        // `Dexie.waitFor` because reading a Blob is not an IndexedDB request:
        // without it the upgrade transaction would auto-commit under the await
        // and the put after it would land on a closed transaction.
        //
        // The unit of failure is ONE KEY. The keys come first (a `primaryKeys`
        // read touches no values), and each row is then fetched by its own
        // `get`, so a record WebKit cannot even hand back — the request itself
        // failing, not a Blob that reads empty — gets a best-effort delete by
        // key and the walk goes on; if that delete fails too the record is
        // left where it is, and every reader of the table skips it through
        // `hasPhotoBuffer`. One `toArray()` would make that record abort the
        // whole version-2 transaction; Dexie would then fail `open()`, and
        // since nothing opens the store eagerly, every store call would reject
        // for the rest of that launch.
        const table = tx.table<LegacyPhotoBlobRow | PhotoBlobRow, string>('photoBlobs');
        const keys = await table.toCollection().primaryKeys();
        for (const key of keys) {
          let row: LegacyPhotoBlobRow | PhotoBlobRow | undefined;
          try {
            row = await table.get(key);
          } catch {
            // Best effort: a record that can be neither read nor deleted is
            // left where it is and the walk goes on, because a throw here
            // would re-abort the whole version-2 transaction.
            try {
              await table.delete(key);
            } catch {
              // left in place, on purpose
            }
            continue;
          }
          if (!row) continue;
          if (hasPhotoBuffer(row)) continue;
          const legacy = row as Partial<LegacyPhotoBlobRow>;
          const data =
            legacy.blob instanceof Blob
              ? await Dexie.waitFor(photoBufferOf(legacy.blob))
              : new ArrayBuffer(0);
          if (data.byteLength === 0) {
            await table.delete(row.photoId);
            continue;
          }
          await table.put({
            photoId: row.photoId,
            surveyId: row.surveyId,
            data,
            contentType: row.contentType,
            bytes: row.bytes,
            capturedAt: row.capturedAt,
          });
        }
      });

    // D-059: a document is handed out in the catalog's CURRENT answer shapes,
    // whatever the row holds. Runs on every read of the surveys table — inside
    // `updateDocument`'s read-modify-write too, so the next write persists the
    // migrated shape and the office never receives the old one again. Same
    // object back when there is nothing to migrate (the common path).
    this.surveys.hook('reading', (row) => {
      // Dexie fires this for a missing key too (`get` → undefined).
      if (!row) return row;
      const document = migrateLegacyAnswers(row.document);
      return document === row.document ? row : { ...row, document };
    });
  }
}

/* ──────────────────────────────── options ──────────────────────────────── */

export interface SurveyStoreOptions {
  /** Injected so tests can freeze time. Defaults to the real clock. */
  clock?: Clock;
}

/** Fired after a write transaction commits. Drives the sync pill's counters. */
export type StoreListener = () => void;

export interface UpdateDocumentOptions {
  /**
   * Whether the change is survey CONTENT that the server needs.
   *
   * `false` marks the write as local transport bookkeeping — today only photo
   * upload state. `PhotoRef.uploadState` describes where the bytes are, not what
   * the surveyor found, and the server maintains its own copy of it (see
   * `recordPhoto` / `markPhotoUploaded` in queries.ts). Dirtying the document
   * for it would enqueue a document push after every photo upload, and each of
   * those pushes would itself change nothing the office cares about.
   *
   * Defaults to `true`: a write is content unless it says otherwise.
   */
  content?: boolean;
}

/**
 * What an `updateDocument` mutate returns to say "I changed nothing" — D-056.
 *
 * A guarded write can decide, inside the transaction, that it has nothing to
 * do: D-055's dropped write (a typed child committed as its parent's No hid
 * it), or the dirty-pits count landing on an unticked row. Without this the
 * store could not tell that apart from a real change, so it bumped
 * `localRevision`, dirtied the survey and queued a push of an identical
 * document. A symbol, not `false`: an arrow body like `(d) => (d.x = false)`
 * returns false by accident and is a real write.
 */
export const NO_CHANGE: unique symbol = Symbol('updateDocument: no change');
export type DocumentMutate = (document: SurveyDocument) => void | typeof NO_CHANGE;

/* ────────────────────────────── the store ────────────────────────────── */

export class SurveyStore {
  readonly db: UpSurvDb;
  private readonly clock: Clock;
  private readonly listeners = new Set<StoreListener>();

  constructor(db: UpSurvDb, options: SurveyStoreOptions = {}) {
    this.db = db;
    this.clock = options.clock ?? systemClock;
  }

  /** Open (or reopen) a store over a named IndexedDB database. */
  static open(name: string = DB_NAME, options: SurveyStoreOptions = {}): SurveyStore {
    return new SurveyStore(new UpSurvDb(name), options);
  }

  /** Close the underlying connection. Reopening the same name sees every row. */
  close(): void {
    this.db.close();
  }

  /** Drop the database entirely. Test teardown; never called in the field. */
  async destroy(): Promise<void> {
    await this.db.delete();
  }

  now(): number {
    return this.clock();
  }

  /* ───────────────────────────── change feed ───────────────────────────── */

  /**
   * Notified after every committed write. The sync engine uses this to keep the
   * always-visible status pill's queued count honest without polling.
   */
  subscribe(listener: StoreListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Always called OUTSIDE the transaction — a listener must not join it. */
  private emit(): void {
    for (const listener of [...this.listeners]) listener();
  }

  /* ──────────────────────────────── reads ──────────────────────────────── */

  async getSurvey(surveyId: string): Promise<StoredSurvey | undefined> {
    return this.db.surveys.get(surveyId);
  }

  async getDocument(surveyId: string): Promise<SurveyDocument | undefined> {
    const record = await this.db.surveys.get(surveyId);
    return record?.document;
  }

  async listSurveys(): Promise<StoredSurvey[]> {
    const rows = await this.db.surveys.toArray();
    return rows.sort((a, b) => (a.surveyId < b.surveyId ? -1 : a.surveyId > b.surveyId ? 1 : 0));
  }

  async listDirtySurveys(): Promise<StoredSurvey[]> {
    return this.db.surveys.where('dirty').equals(1).toArray();
  }

  /**
   * The row as a Blob-shaped record; the buffer never leaves this module bare.
   * A row with no buffer — one the upgrade could neither read nor delete — is
   * reported as absent, so the R2 fallback refills it rather than a reader
   * being handed an empty picture.
   */
  async getPhotoBlob(photoId: string): Promise<StoredPhotoBlob | undefined> {
    const row = await this.db.photoBlobs.get(photoId);
    return hasPhotoBuffer(row) ? storedPhotoBlobOf(row) : undefined;
  }

  /**
   * What this device is holding, in the two numbers the surveyor and the office
   * both ask about (D-020).
   *
   * `waiting` counts SURVEYS, not rows: "3 waiting to send" is a sentence about
   * work, and a survey with eleven queued photos is one job that has not landed,
   * not eleven. A survey counts as waiting if it is dirty OR it still owns an
   * outbox row — the two are not the same fact, and either one alone would
   * undercount. A discarded survey leaves a row behind with no document, and
   * that row is still something this device owes the office, so it counts too.
   */
  async storageSnapshot(): Promise<StorageSnapshot> {
    const [surveys, rows] = await Promise.all([
      this.db.surveys.toArray(),
      this.db.outbox.toArray(),
    ]);
    // The photo rows are walked with a cursor, one at a time: since version 2
    // a row IS its picture, and `toArray()` would hold every photo on the
    // phone in memory at once for the sake of two numbers.
    let photos = 0;
    let photoBytes = 0;
    await this.db.photoBlobs.each((row) => {
      // A row with no buffer holds no picture, so it is neither a photo nor bytes.
      if (!hasPhotoBuffer(row)) return;
      photos += 1;
      // The bytes actually on the phone, not the count the PhotoRef declared.
      photoBytes += row.data.byteLength;
    });
    const waiting = new Set<string>();
    for (const record of surveys) if (record.dirty === 1) waiting.add(record.surveyId);
    for (const row of rows) waiting.add(row.surveyId);
    return {
      surveys: surveys.length,
      waiting: waiting.size,
      queuedItems: rows.length,
      photos,
      photoBytes,
    };
  }

  /** The whole outbox in drain order. Small by construction — tens of rows. */
  async listOutbox(): Promise<OutboxItem[]> {
    const rows = await this.db.outbox.toArray();
    return rows.sort(compareOutbox);
  }

  async listOutboxFor(surveyId: string): Promise<OutboxItem[]> {
    const rows = await this.db.outbox.where('surveyId').equals(surveyId).toArray();
    return rows.sort(compareOutbox);
  }

  async getOutboxItem(id: string): Promise<OutboxItem | undefined> {
    return this.db.outbox.get(id);
  }

  async countOutbox(): Promise<number> {
    return this.db.outbox.count();
  }

  async countOutboxByKind(kind: OutboxKind): Promise<number> {
    return this.db.outbox.where('kind').equals(kind).count();
  }

  /**
   * Rows parked because the server refused the payload itself. These are the
   * ones the surveyor has to decide about; everything else is just waiting for
   * signal. Sorted in drain order so the surfaced list is stable across renders.
   */
  async listBlocked(): Promise<OutboxItem[]> {
    const rows = await this.db.outbox.where('state').equals('blocked').toArray();
    return rows.sort(compareOutbox);
  }

  async countBlocked(): Promise<number> {
    return this.db.outbox.where('state').equals('blocked').count();
  }

  /**
   * How much this survey still owes the server.
   *
   * Read by the assignment inbox before it lets a pulled server copy replace a
   * local one: a survey can be `dirty === 0` and still hold a queued photo whose
   * bytes have never left the phone, and adopting over it would strand that
   * photo's PhotoRef (`sendPhoto` would then find no metadata and drop the row).
   * "Nothing unsynced" has to mean the outbox too, not just the dirty flag.
   */
  async countOutboxFor(surveyId: string): Promise<number> {
    return this.db.outbox.where('surveyId').equals(surveyId).count();
  }

  /**
   * What every survey on this device still owes the office, by id (D-052 §2).
   *
   * One pass over the outbox for the Home screen's *Submitted* rows, which
   * used to say "Submitted" the instant the button was tapped and nothing more
   * while the photos and the seal behind them (D-050) sat on the phone. Rows
   * are split the way the pill splits them: `sending` will go on its own,
   * `held` is parked and waits for a person. Thumbs are not counted — they
   * travel BEHIND the seal (KIND_PRIORITY), so a survey whose only remaining
   * rows are thumbs is already in at the office, and the row must not say
   * otherwise. A survey with no rows at all is absent from the map.
   */
  async outboxOwedBySurvey(): Promise<Map<string, OutboxOwed>> {
    const owed = new Map<string, OutboxOwed>();
    for (const row of await this.db.outbox.toArray()) {
      if (row.kind === 'photo_thumb') continue;
      const entry = owed.get(row.surveyId) ?? { sending: 0, held: 0 };
      if (row.state === 'blocked') entry.held += 1;
      else entry.sending += 1;
      owed.set(row.surveyId, entry);
    }
    return owed;
  }

  /**
   * Rows that are CLAIMABLE right now, in drain order: `queued` and past their
   * backoff.
   *
   * The `state` half of that predicate is what makes `recoverInFlight()` real.
   * A row is `in_flight` only while a send is outstanding, and every settled
   * path returns it — ack deletes it, `failOutboxItem` puts it back to `queued`.
   * The one path that does not settle is the tab dying mid-send, and that row
   * stays `in_flight` forever. Skipping it here is deliberate: a drain must not
   * hand the same row to the transport twice (two tabs, or a reload racing an
   * outstanding request), and the recovery of an abandoned claim belongs to
   * `recoverInFlight()` on `start()` — one place, not two.
   *
   * If this filter is ever dropped, `recoverInFlight()` becomes decorative and
   * §6.3's "actually retried" clause quietly loses its mechanism. See the test
   * "a stubbed recoverInFlight strands the row" in tests/unit/sync.test.ts.
   */
  async dueOutbox(nowMs: number = this.now()): Promise<OutboxItem[]> {
    const rows = await this.db.outbox.toArray();
    return rows
      .filter((row) => row.state === 'queued' && row.nextAttemptAt <= nowMs)
      .sort(compareOutbox);
  }

  /* ─────────────────────────── survey lifecycle ─────────────────────────── */

  /**
   * Put a document the DEVICE authored — a field-initiated survey, or the first
   * local edit of an assignment. Dirty from birth and immediately queued.
   *
   * A field-initiated survey also queues its CREATION NOTICE here, in the same
   * transaction that writes the document. From this instant spec §2.1's promise
   * is a durable row: the office learns about the job on the first drain that
   * reaches the network, whether that is this second or next Tuesday, and
   * nothing that happens to the survey in between can delete it.
   */
  async createSurvey(document: SurveyDocument): Promise<StoredSurvey> {
    const nowMs = this.now();
    const record = await this.db.transaction('rw', this.db.surveys, this.db.outbox, async () => {
      const existing = await this.db.surveys.get(document.surveyId);
      const localRevision = (existing?.localRevision ?? 0) + 1;
      const next: StoredSurvey = {
        surveyId: document.surveyId,
        document,
        status: document.status,
        localRevision,
        syncedRevision: existing?.syncedRevision ?? 0,
        serverRevision: existing?.serverRevision ?? 0,
        dirty: 1,
        sealed: existing?.sealed ?? 0,
        creationNotified: existing?.creationNotified ?? 0,
        updatedAt: document.updatedAt,
        ...(existing?.lastSyncedAt ? { lastSyncedAt: existing.lastSyncedAt } : {}),
      };
      await this.db.surveys.put(next);
      await this.queueCreationNotice(next, nowMs);
      await this.queueDocumentPush(next, nowMs);
      return next;
    });
    this.emit();
    return record;
  }

  /**
   * Accept a document the SERVER is authoritative for — an assignment pulled
   * down, or a sealed submission the device has to defer to. Lands CLEAN: there
   * is nothing local to push, so nothing is queued.
   *
   * Callers must run resolveConflict() (sync.ts) first. This method applies a
   * decision; it does not make one.
   */
  async adoptServerDocument(
    document: SurveyDocument,
    serverRevision: number,
    sealed = false,
  ): Promise<StoredSurvey> {
    const nowMs = this.now();
    const record = await this.db.transaction('rw', this.db.surveys, this.db.outbox, async () => {
      const existing = await this.db.surveys.get(document.surveyId);
      const localRevision = (existing?.localRevision ?? 0) + 1;
      const next: StoredSurvey = {
        surveyId: document.surveyId,
        document,
        status: document.status,
        localRevision,
        syncedRevision: localRevision,
        serverRevision,
        dirty: 0,
        sealed: sealed ? 1 : 0,
        // Carried, never assumed. The server holding a document proves a row
        // exists (a document push upserts one); it does not prove anyone was
        // told the job exists, which is a notification and a different fact.
        creationNotified: existing?.creationNotified ?? 0,
        updatedAt: document.updatedAt,
        lastSyncedAt: isoAt(nowMs),
      };
      await this.db.surveys.put(next);
      // The local copy no longer diverges, so any queued push for it is stale.
      // Photo rows are untouched: the bytes still owe R2 either way, and so is
      // any outstanding creation notice.
      await this.db.outbox.delete(outboxId.document(document.surveyId));
      if (sealed) await this.db.outbox.delete(outboxId.submission(document.surveyId));
      return next;
    });
    this.emit();
    return record;
  }

  /**
   * The single write primitive. Everything that changes a survey goes through
   * here: read, mutate, stamp, persist, queue — one transaction, no gap.
   */
  async updateDocument(
    surveyId: string,
    mutate: DocumentMutate,
    options: UpdateDocumentOptions = {},
  ): Promise<StoredSurvey> {
    const isContent = options.content ?? true;
    const nowMs = this.now();
    let changed = true;
    const record = await this.db.transaction('rw', this.db.surveys, this.db.outbox, async () => {
      const existing = await this.db.surveys.get(surveyId);
      if (!existing) throw new Error(`updateDocument: unknown survey "${surveyId}"`);

      // Dexie hands back a structured clone, so mutating it cannot reach any
      // object the caller is still holding.
      const document = existing.document;
      if (mutate(document) === NO_CHANGE) {
        // D-056: nothing stored, nothing stamped, nothing queued. A mutate
        // returns NO_CHANGE only when it has touched nothing.
        changed = false;
        return existing;
      }

      // A bookkeeping write does not advance the revision at all. `localRevision`
      // counts changes to survey CONTENT, and a photo's upload state is not
      // content — inflating the revision for it would make a clean survey look
      // dirty and would invalidate the sentRevision guard in ackDocumentPush.
      const localRevision = isContent ? existing.localRevision + 1 : existing.localRevision;
      if (isContent) document.updatedAt = isoAt(nowMs);

      const next: StoredSurvey = {
        ...existing,
        document,
        status: document.status,
        localRevision,
        dirty: isContent ? 1 : existing.dirty,
        updatedAt: document.updatedAt,
      };
      await this.db.surveys.put(next);
      if (isContent) await this.queueDocumentPush(next, nowMs);
      // D-049: the write that seals the survey IS the promise to tell the
      // office. Before this, `enqueueSubmission` was a second transaction
      // after this one, and a phone cut off between the two (VA Tuskegee,
      // 2026-09-15) was left sealed with nothing to send: `sendDocument`
      // drops a submitted document's own push on purpose, so no row carried
      // the news and the pill read Synced. One transaction, or neither.
      if (isContent && isSubmittedStatus(next.status) && next.sealed !== 1) {
        await this.putSubmissionRow(next, nowMs);
      }
      return next;
    });
    if (changed) this.emit();
    return record;
  }

  /* ──────────────────────── importing a survey (D-020) ──────────────────── */

  /**
   * Land surveys that came from ANOTHER container — the export/import bridge.
   *
   * ── Why this is not `createSurvey` in a loop ────────────────────────────────
   * `createSurvey` writes a document the device just authored: dirty from birth,
   * one document push queued, no photo bytes, no idea whether the office has
   * heard of it. An imported survey is none of those things. It arrives with a
   * history — bytes, a server revision, possibly a seal — and every one of those
   * facts decides what this device may now do with it. Reconstructing them by
   * calling the ordinary write path and then patching the row afterwards would
   * leave a window in which a sealed survey was dirty and queued for a push the
   * server would refuse.
   *
   * ── ONE transaction PER SURVEY ─────────────────────────────────────────────
   * "Never half-imports" is a promise about each survey: its document, its
   * photo bytes and its queued rows commit together or none of them do. It
   * used to be a promise about the whole file — one transaction for every
   * survey in it — and that meant reading EVERY photo in the file into memory
   * first (a Blob read is not an IndexedDB request, and awaiting one inside a
   * Dexie transaction ends it): a 415 MB phone importing its own backup held
   * 415 MB of buffers beside the file, which is an iOS tab kill on the one
   * path a phone in trouble depends on (final review's fix wave, D-040
   * postscript). Now each survey's slices are read, written and released
   * before the next survey's are touched, so the high-water mark is one
   * survey's pictures — a real bound only when the file holds several surveys;
   * a single 415 MB survey still peaks at 415 MB here, and reading each slice
   * inside the transaction under Dexie.waitFor (one picture at a time) is the
   * deferred follow-up. A survey whose transaction fails is refused by name in
   * the result and the surveys after it still land; the caller must have
   * sliced its blobs lazily (`Blob.slice()` allocates nothing) for the same
   * reason as before.
   *
   * ── What is carried, and why each one ──────────────────────────────────────
   *   serverRevision / creationNotified / lastSyncedAt
   *     Facts about the SERVER, not about this device. Dropping them would tell
   *     `removalPathFor` the office has never heard of a survey it is holding,
   *     and D-012's "delete, it's the only copy" would then be a lie.
   *   sealed
   *     Carried, and NEVER cleared. `sealed || existing.sealed` in that order:
   *     an import may seal a survey this device thought was open, and may not
   *     open one this device knows is sealed (D-012/D-018). Export is not a
   *     laundering route around the seal.
   *   the photo BYTES
   *     The point of the exercise. A rescued survey with no photos is not a
   *     rescued survey.
   *
   * ── What is queued ─────────────────────────────────────────────────────────
   * Exactly what an ordinary survey in the same state would have queued, so a
   * rescued survey syncs and submits like a native one:
   *   the creation notice, if it is field-initiated and was never acknowledged;
   *   a SUBMISSION row if the document is submitted but the seal never came
   *     back — that is D-018's stranded survey, and the whole reason it is worth
   *     moving is that it can still be sent from the device it lands on;
   *   a document push otherwise (`queueDocumentPush` refuses a submitted one);
   *   a photo upload for every photo whose bytes are here and are not in R2 yet
   *     — including on a sealed survey, exactly as `ackSubmission` keeps them.
   */
  async importSurveys(inputs: readonly ImportedSurvey[]): Promise<ImportResult> {
    const nowMs = this.now();
    const written: StoredSurvey[] = [];
    const refused: ImportRefusal[] = [];

    for (const input of inputs) {
      const surveyId = input.document.surveyId;
      // This survey's slices are read into buffers HERE, before its
      // transaction, because a Blob read is not an IndexedDB request and would
      // let the transaction auto-commit under it — and only this survey's, so
      // the buffers of the one before are already garbage by now. A slice that
      // reads empty is a photo the source device had already lost (a version-1
      // row WebKit emptied, exported as zero bytes): it is left out, so the
      // survey lands with that photo counted as "without bytes" rather than
      // with an empty row that every reader would trip over.
      const buffers = new Map<string, ArrayBuffer>();
      for (const photo of input.photos) {
        const data = await photoBufferOf(photo.blob);
        if (data.byteLength > 0) buffers.set(photo.photoId, data);
      }

      try {
        written.push(await this.importOneSurvey(input, buffers, nowMs));
      } catch (cause) {
        // Refused by name, and the loop goes on: the surveys after this one
        // are not sunk by it, and the sentence the surveyor reads says which
        // one is not on the phone and why.
        refused.push({ surveyId, message: cause instanceof Error ? cause.message : String(cause) });
        continue;
      }
      // Emitted per survey, so My Surveys fills in as a long file lands.
      this.emit();
    }

    return { written, refused };
  }

  /** One survey's transaction — see `importSurveys` for what is carried and why. */
  private async importOneSurvey(
    input: ImportedSurvey,
    buffers: ReadonlyMap<string, ArrayBuffer>,
    nowMs: number,
  ): Promise<StoredSurvey> {
    return this.db.transaction(
      'rw',
      this.db.surveys,
      this.db.photoBlobs,
      this.db.photoThumbs,
      this.db.outbox,
      async () => {
        {
          const surveyId = input.document.surveyId;
          const existing = await this.db.surveys.get(surveyId);
          const localRevision = (existing?.localRevision ?? 0) + 1;
          const sealed: 0 | 1 = input.sealed || existing?.sealed === 1 ? 1 : 0;
          const lastSyncedAt = input.lastSyncedAt ?? existing?.lastSyncedAt;

          const next: StoredSurvey = {
            surveyId,
            document: input.document,
            status: input.document.status,
            localRevision,
            // A sealed survey owes the server nothing, so it lands clean.
            // Anything else lands DIRTY on purpose: this device has never sent
            // a byte of it, whatever the device it came from had managed.
            syncedRevision: sealed === 1 ? localRevision : 0,
            serverRevision: Math.max(existing?.serverRevision ?? 0, input.serverRevision),
            dirty: sealed === 1 ? 0 : 1,
            sealed,
            creationNotified: input.creationNotified || existing?.creationNotified === 1 ? 1 : 0,
            updatedAt: input.document.updatedAt,
            ...(lastSyncedAt ? { lastSyncedAt } : {}),
          };

          // Photo bytes this device already held for a survey being replaced are
          // dropped first: the incoming document is the whole truth about which
          // photos exist, and a blob whose PhotoRef is gone is bytes nobody can
          // ever look at or upload. The thumbs go with them — a thumb never
          // outlives its picture, and the file carries none to replace them.
          const stale = await this.db.photoBlobs.where('surveyId').equals(surveyId).primaryKeys();
          if (stale.length > 0) await this.db.photoBlobs.bulkDelete(stale);
          const staleThumbs = await this.db.photoThumbs.where('surveyId').equals(surveyId).primaryKeys();
          if (staleThumbs.length > 0) await this.db.photoThumbs.bulkDelete(staleThumbs);

          await this.db.surveys.put(next);
          for (const photo of input.photos) {
            const data = buffers.get(photo.photoId);
            if (!data) continue;
            await this.db.photoBlobs.put({
              photoId: photo.photoId,
              surveyId,
              data,
              contentType: photo.contentType,
              bytes: photo.bytes,
              capturedAt: photo.capturedAt,
            });
          }

          await this.queueCreationNotice(next, nowMs);
          if (isSubmittedStatus(next.status) && sealed === 0) {
            await this.db.outbox.put({
              id: outboxId.submission(surveyId),
              kind: 'submission',
              surveyId,
              localRevision,
              state: 'queued',
              attempts: 0,
              createdAt: isoAt(nowMs),
              nextAttemptAt: nowMs,
            });
            await this.putSealRow(surveyId, localRevision, nowMs);
          }
          await this.queueDocumentPush(next, nowMs);

          // Only a photo whose bytes actually landed is queued: an upload row
          // for an empty slice would only park under PHOTO_BYTES_MISSING.
          for (const photo of next.document.photos) {
            if (photo.uploadState === 'uploaded') continue;
            if (!buffers.has(photo.id)) continue;
            await this.queuePhotoUpload(surveyId, photo.id, localRevision, nowMs);
          }

          return next;
        }
      },
    );
  }

  /* ─────────────────────── removing a survey (D-012) ────────────────────── */

  /**
   * TRUE LOCAL DELETE — a survey the server has never heard of.
   *
   * The document, the photo bytes and every outbox row it owns go in ONE
   * transaction. Nothing is queued, because there is nothing to tell: this
   * survey existed on this phone and nowhere else, and the `create` row that
   * would have announced it is deleted here before it ever ran.
   *
   * SUBMITTED counts, when the submission never left (D-018). A survey closed
   * and submitted on an unlinked phone — or in a basement that never saw a bar
   * of signal — is sealed locally and absent from the office entirely, so the
   * admin page D-012 named as its only removal authority cannot see it to
   * remove it. The queued `submission` row is deleted here along with every
   * other row for the survey, so nothing is left to deliver a record the
   * surveyor has just said they do not want.
   *
   * Refuses anything else. `removalPathFor` reads real state — an acknowledged
   * server revision, an acknowledged creation notice, an acknowledged seal, or
   * an office-created origin — so this cannot silently destroy the only copy of
   * work the office is waiting on. A survey the server knows about goes through
   * `discardSurvey`, which tells it, or through the admin page once submitted.
   */
  async deleteSurveyLocal(surveyId: string): Promise<void> {
    await this.db.transaction(
      'rw',
      this.db.surveys,
      this.db.photoBlobs,
      this.db.photoThumbs,
      this.db.outbox,
      async () => {
        const existing = await this.db.surveys.get(surveyId);
        if (!existing) return;
        const path = removalPathFor(existing);
        if (path !== 'local_delete') {
          throw new Error(
            `deleteSurveyLocal: survey "${surveyId}" has reached the server (${path}); ` +
              'use discardSurvey so the office is told',
          );
        }
        await this.purgeLocal(surveyId);
      },
    );
    this.emit();
  }

  /**
   * DISCARD — a survey the server holds and the surveyor is withdrawing.
   *
   * Locally identical to a delete: document, photo bytes and every outbox row
   * for this survey go, so it is gone from every list on the device the instant
   * this returns. The difference is the row left behind — one `discard` outbox
   * row, which is the durable promise to tell the server.
   *
   * That row is why this is offline-safe. With no signal the drain skips it, it
   * survives a reload like every other queued mutation, and the first drain that
   * reaches the network marks the server record discarded. It is also the
   * device's TOMBSTONE in the meantime: `pullAssignments` refuses to re-add a
   * survey with a discard queued, because until the server has been told it is
   * still listing the survey, and R0 ("nothing local to protect") would
   * otherwise walk it straight back onto the phone.
   *
   * Refuses a submitted survey — every submitted survey, and the test is the
   * STATUS rather than the removal path (D-018). A submission that never left
   * the phone is removable by `deleteSurveyLocal`, but it is not discardable by
   * this method under any circumstance: a discard promises "the office keeps the
   * record and marks it withdrawn", and there is no office record to mark. The
   * two must not blur, because the sentence on screen differs.
   */
  async discardSurvey(surveyId: string): Promise<void> {
    const nowMs = this.now();
    await this.db.transaction(
      'rw',
      this.db.surveys,
      this.db.photoBlobs,
      this.db.photoThumbs,
      this.db.outbox,
      async () => {
        const existing = await this.db.surveys.get(surveyId);
        if (!existing) return;
        if (existing.sealed === 1 || isSubmittedStatus(existing.status)) {
          throw new Error(
            `discardSurvey: survey "${surveyId}" is submitted; ` +
              (removalPathFor(existing) === 'local_delete'
                ? 'it never reached the office — use deleteSurveyLocal'
                : 'only the office can remove it'),
          );
        }
        await this.purgeLocal(surveyId);
        // Queued AFTER the purge, which deleted every row for this survey —
        // including any earlier discard row, so a second discard coalesces onto
        // one promise rather than sending twice.
        await this.db.outbox.put({
          id: outboxId.discard(surveyId),
          kind: 'discard',
          surveyId,
          localRevision: existing.localRevision,
          state: 'queued',
          attempts: 0,
          createdAt: isoAt(nowMs),
          nextAttemptAt: nowMs,
        });
      },
    );
    this.emit();
  }

  /**
   * Everything this device holds for one survey. Caller supplies the
   * transaction; both removal paths above run it and differ only in what they
   * queue afterwards.
   */
  private async purgeLocal(surveyId: string): Promise<void> {
    const blobs = await this.db.photoBlobs.where('surveyId').equals(surveyId).primaryKeys();
    const thumbs = await this.db.photoThumbs.where('surveyId').equals(surveyId).primaryKeys();
    const rows = await this.db.outbox.where('surveyId').equals(surveyId).primaryKeys();
    await this.db.surveys.delete(surveyId);
    if (blobs.length > 0) await this.db.photoBlobs.bulkDelete(blobs);
    if (thumbs.length > 0) await this.db.photoThumbs.bulkDelete(thumbs);
    if (rows.length > 0) await this.db.outbox.bulkDelete(rows);
  }

  /**
   * Is a discard still owed to the server for this survey?
   *
   * The pull path asks before it adopts anything: while this is true the server
   * is still listing a survey the surveyor has already withdrawn, and adopting
   * that listing would resurrect it.
   */
  async hasPendingDiscard(surveyId: string): Promise<boolean> {
    return (await this.db.outbox.get(outboxId.discard(surveyId))) !== undefined;
  }

  /** Apply a discard ack: the server has been told, the promise is kept. */
  async ackDiscard(surveyId: string): Promise<void> {
    await this.db.outbox.delete(outboxId.discard(surveyId));
    this.emit();
  }

  /* ──────────────────────────── answer writes ──────────────────────────── */

  /**
   * Persist one answer. This is the hot path — it runs on every tap — and it
   * commits to IndexedDB before it returns. Nothing is held only in React state.
   */
  async writeAnswer(
    surveyId: string,
    key: AnswerKey,
    value: AnswerValue,
  ): Promise<StoredSurvey> {
    return this.updateDocument(surveyId, (document) => {
      document.answers[key] = value;
    });
  }

  /** Remove an answer — a gate closed and its dependent answer no longer applies. */
  async clearAnswer(surveyId: string, key: AnswerKey): Promise<StoredSurvey> {
    return this.updateDocument(surveyId, (document) => {
      delete document.answers[key];
    });
  }

  async addNote(surveyId: string, note: Note): Promise<StoredSurvey> {
    return this.updateDocument(surveyId, (document) => {
      const at = document.notes.findIndex((n) => n.id === note.id);
      if (at >= 0) document.notes[at] = note;
      else document.notes.push(note);
    });
  }

  /* ───────────────────────────────  photos  ─────────────────────────────── */

  /**
   * Capture a photo: metadata into the document, bytes into `photoBlobs`, upload
   * into the outbox — one transaction. Either the surveyor has a photo that is
   * fully accounted for or the tap never happened; there is no state where the
   * bytes exist but nothing knows to upload them.
   *
   * `thumb` (round 8.1, task 3) is the 640-px JPEG the capture screen made,
   * or null when this phone could not make one (a HEIC it cannot decode). A
   * thumb goes into `photoThumbs` and queues its own `photo_thumb` row behind
   * the photo's, in the same transaction, so a thumb on disk is always a thumb
   * the office will be sent. No thumb queues nothing: the photo is complete
   * without one.
   */
  async addPhoto(
    surveyId: string,
    photo: PhotoRef,
    blob: Blob,
    thumb: ArrayBuffer | null = null,
  ): Promise<StoredSurvey> {
    if (photo.surveyId !== surveyId) {
      // schema.ts rejects this at the wire boundary; catching it here means the
      // caller learns at capture time instead of at submit time.
      throw new Error(
        `addPhoto: photo "${photo.id}" is tagged to survey "${photo.surveyId}" ` +
          `but was added to "${surveyId}"`,
      );
    }

    /*
     * VALIDATE AT CAPTURE, NOT AT DRAIN (the 2026-08-06 incident).
     *
     * This is the single choke point every capture route runs through — the
     * camera button, the camera roll, a shot-list row, a question, an ad hoc
     * item — so it is the one place that can promise the device never enqueues
     * a photo the office cannot accept. Before this, a malformed PhotoRef was
     * discovered by the DRAIN: hours later, in a basement, by a retry loop, with
     * nothing on screen the surveyor could act on.
     *
     * Here, they are still holding the phone and still standing in front of the
     * thing they photographed. `PhotoCapture` catches this and shows it, so the
     * worst case is "that one did not save, take it again" instead of a red pill
     * on Wednesday. The bytes are not written and no outbox row is created, so
     * there is nothing left half-done.
     */
    const shape = PhotoRefSchema.safeParse(photo);
    if (!shape.success) {
      const where = shape.error.issues
        .map((issue) => `${issue.path.join('.') || 'photo'} — ${issue.message}`)
        .join('; ');
      throw new Error(`Could not save that photo — the office would refuse it (${where}).`);
    }

    // Read BEFORE the transaction: a Blob read is not an IndexedDB request and
    // would let the transaction auto-commit underneath it. `PhotoCapture`
    // already refuses a File it cannot read, so an empty read here is a
    // programming error on some other capture route, and it is refused the
    // same way rather than stored as the empty row WebKit taught us to fear.
    const data = await photoBufferOf(blob);
    if (data.byteLength === 0) {
      throw new Error('Could not save that photo — the phone could not read the picture.');
    }
    // An empty thumb is no thumb: nothing is written and nothing is queued.
    const thumbData = thumb && thumb.byteLength > 0 ? thumb : null;
    const nowMs = this.now();
    const record = await this.db.transaction(
      'rw',
      this.db.surveys,
      this.db.photoBlobs,
      this.db.photoThumbs,
      this.db.outbox,
      async () => {
        const existing = await this.db.surveys.get(surveyId);
        if (!existing) throw new Error(`addPhoto: unknown survey "${surveyId}"`);

        const document = existing.document;
        const at = document.photos.findIndex((p) => p.id === photo.id);
        if (at >= 0) document.photos[at] = photo;
        else document.photos.push(photo);
        document.updatedAt = isoAt(nowMs);

        const localRevision = existing.localRevision + 1;
        const next: StoredSurvey = {
          ...existing,
          document,
          status: document.status,
          localRevision,
          dirty: 1,
          updatedAt: document.updatedAt,
        };
        await this.db.surveys.put(next);
        await this.writePhotoBlob(surveyId, photo, data);
        await this.queueDocumentPush(next, nowMs);
        await this.queuePhotoUpload(surveyId, photo.id, localRevision, nowMs);
        if (thumbData) {
          await this.writePhotoThumb(surveyId, photo.id, thumbData);
          await this.queuePhotoThumbUpload(surveyId, photo.id, localRevision, nowMs);
        }
        return next;
      },
    );
    this.emit();
    return record;
  }

  /**
   * Remove a photo the surveyor deleted: the metadata, the bytes, and the queued
   * upload — one transaction, so there is no state where the document has
   * forgotten a photo the outbox is still trying to push to R2.
   *
   * This is CONTENT (unlike `setPhotoUploadState`): "there is no photo of the
   * controller" is a fact about the survey, and the server has to be told.
   *
   * Deliberately does NOT reach into R2. A photo whose bytes already uploaded
   * leaves an orphaned object; that is a janitor's problem and is far cheaper
   * than a delete path that can fail halfway with no signal.
   */
  async deletePhoto(surveyId: string, photoId: string): Promise<StoredSurvey | undefined> {
    const nowMs = this.now();
    const record = await this.db.transaction(
      'rw',
      this.db.surveys,
      this.db.photoBlobs,
      this.db.photoThumbs,
      this.db.outbox,
      async () => {
        const existing = await this.db.surveys.get(surveyId);
        if (!existing) return undefined;

        const document = existing.document;
        const at = document.photos.findIndex((p) => p.id === photoId);
        if (at < 0) return existing;
        document.photos.splice(at, 1);
        document.updatedAt = isoAt(nowMs);

        const next: StoredSurvey = {
          ...existing,
          document,
          status: document.status,
          localRevision: existing.localRevision + 1,
          dirty: 1,
          updatedAt: document.updatedAt,
        };
        await this.db.surveys.put(next);
        await this.db.photoBlobs.delete(photoId);
        await this.db.outbox.delete(outboxId.photo(photoId));
        // The thumb and its row go with the picture: neither means anything
        // without it, and the office refuses a thumb for a photo it never sees.
        await this.db.photoThumbs.delete(photoId);
        await this.db.outbox.delete(outboxId.photoThumb(photoId));
        await this.queueDocumentPush(next, nowMs);
        return next;
      },
    );
    this.emit();
    return record;
  }

  /**
   * Record where a photo's bytes are. Bookkeeping, not content — see
   * UpdateDocumentOptions.content for why this does not dirty the document.
   */
  async setPhotoUploadState(
    surveyId: string,
    photoId: string,
    uploadState: PhotoUploadState,
    attempts?: number,
  ): Promise<StoredSurvey> {
    return this.updateDocument(
      surveyId,
      (document) => {
        const photo = document.photos.find((p) => p.id === photoId);
        if (!photo) return;
        photo.uploadState = uploadState;
        if (attempts !== undefined) photo.attempts = attempts;
      },
      { content: false },
    );
  }

  /**
   * A caption the surveyor typed for one photo, shown under it on the phone —
   * 2.4.0, D-038. Content, not bookkeeping (unlike `setPhotoUploadState`
   * above), so it goes through `updateDocument`'s default and dirties the
   * document like any other survey fact.
   *
   * An empty or whitespace-only label REMOVES the field rather than storing
   * blank text — the same convention as every other free-text field in this
   * codebase (see `.trim()` in `src/ui/state/reckoning.ts` and
   * `src/ui/state/sectionClose.ts`). A photo id that does not exist on this
   * document is a no-op, the same as `setPhotoUploadState`.
   */
  async setPhotoLabel(
    surveyId: string,
    photoId: string,
    label: string | undefined,
  ): Promise<StoredSurvey> {
    const trimmed = label?.trim();
    return this.updateDocument(surveyId, (document) => {
      const photo = document.photos.find((p) => p.id === photoId);
      if (!photo) return;
      if (trimmed) photo.label = trimmed;
      else delete photo.label;
    });
  }

  /**
   * Reclaim space once bytes are safely in R2. Never called automatically: until
   * the office confirms the submission, the device is the only copy that has
   * certainly survived, and disk is cheaper than a return trip to the building.
   */
  async purgeUploadedBlobs(surveyId: string): Promise<number> {
    const document = await this.getDocument(surveyId);
    if (!document) return 0;
    const uploaded = document.photos
      .filter((p) => p.uploadState === 'uploaded')
      .map((p) => p.id);
    if (uploaded.length === 0) return 0;
    await this.db.photoBlobs.bulkDelete(uploaded);
    this.emit();
    return uploaded.length;
  }

  /**
   * Keep bytes fetched back from R2 for a photo this device has lost — round
   * 8, task 4. The row is the one `addPhoto` writes, described by the
   * document's own PhotoRef, so a picture that came back from the office is
   * indistinguishable from one taken here: the next render reads it locally
   * and the office is asked once. Nothing is queued and nothing goes dirty —
   * the office already has these bytes; that is where they came from.
   *
   * A survey or photo the document no longer carries writes nothing: a blob
   * whose PhotoRef is gone is bytes nobody can ever look at (the same rule the
   * replace path applies when it drops a replaced survey's blobs).
   *
   * No `emit()` here (final review of round 8): nothing in the document
   * changed, and the hook that called this already holds the bytes it is
   * about to show. The listeners would only re-read what they already had —
   * `SurveyProvider` the same document, `useSurveys` the same list, the sync
   * engine the same outbox counts — and on "Mema's House" that was 120
   * whole-shell refreshes in one render. The one reader of `photoBlobs` that
   * counts them, `DeviceScreen`'s storage snapshot, is its own route with no
   * shelf on it and re-reads on mount.
   */
  async putPhotoBlob(photoId: string, surveyId: string, blob: Blob): Promise<boolean> {
    const document = await this.getDocument(surveyId);
    const photo = document?.photos.find((p) => p.id === photoId);
    if (!photo) return false;
    // An empty body from the office is not a picture: nothing is written (a
    // good row, if there is one, keeps its bytes) and the caller hears that
    // nothing landed, so the next render asks R2 again.
    const data = await photoBufferOf(blob);
    if (data.byteLength === 0) return false;
    await this.writePhotoBlob(surveyId, photo, data);
    return true;
  }

  /**
   * The one `photoBlobs` write for a photo the document carries. Takes the
   * buffer, not a Blob: every caller has already read the picture into memory
   * (`photoBufferOf`) and refused it if the read came back empty.
   */
  private async writePhotoBlob(surveyId: string, photo: PhotoRef, data: ArrayBuffer): Promise<void> {
    await this.db.photoBlobs.put({
      photoId: photo.id,
      surveyId,
      data,
      contentType: photo.contentType,
      bytes: photo.bytes,
      capturedAt: photo.capturedAt,
    });
  }

  /**
   * The thumbnail this device holds for one photo, or undefined when it holds
   * none — a photo whose picture this browser could not decode has no row,
   * and that is a photo without a thumb, not a fault. Round 8.1, task 3; the
   * shelf starts reading this in task 4.
   */
  async getPhotoThumb(photoId: string): Promise<PhotoThumbRow | undefined> {
    const row = await this.db.photoThumbs.get(photoId);
    return row && row.data instanceof ArrayBuffer ? row : undefined;
  }

  /**
   * Keep a thumbnail for a photo the document carries — the counterpart of
   * `putPhotoBlob`, for a thumb fetched back from the office or made late.
   * Nothing is queued: `addPhoto` is the one path that queues a thumb's
   * upload, and this is a cache write, not a capture. A photo the document
   * does not carry, or an empty buffer, writes nothing and answers false, and
   * for the same reasons `putPhotoBlob` gives: a thumb without its picture is
   * bytes nobody can look at, and empty is not a picture. No `emit()`, as
   * `putPhotoBlob` has none: nothing in the document changed.
   */
  async putPhotoThumb(photoId: string, surveyId: string, bytes: ArrayBuffer): Promise<boolean> {
    if (bytes.byteLength === 0) return false;
    const document = await this.getDocument(surveyId);
    if (!document?.photos.some((p) => p.id === photoId)) return false;
    await this.writePhotoThumb(surveyId, photoId, bytes);
    return true;
  }

  /** The one `photoThumbs` write. Takes the buffer every caller already holds. */
  private async writePhotoThumb(surveyId: string, photoId: string, data: ArrayBuffer): Promise<void> {
    await this.db.photoThumbs.put({ photoId, surveyId, data, bytes: data.byteLength });
  }

  /* ─────────────────────────────── outbox ─────────────────────────────── */

  /**
   * Queue spec §2.1's creation notice, once, for a survey born on this device.
   *
   * Only field-initiated surveys: an assigned survey was created BY the office,
   * which already knows about it and would be told twice. Skipped once the
   * notice has been acknowledged, so re-writing the document later — the
   * `createSurvey` path is reachable more than once — cannot re-queue a call
   * that has already been made.
   */
  private async queueCreationNotice(record: StoredSurvey, nowMs: number): Promise<void> {
    if (record.document.origin !== 'field_initiated') return;
    if (record.creationNotified === 1) return;
    const id = outboxId.create(record.surveyId);
    const existing = await this.db.outbox.get(id);
    if (existing) return; // the promise is already outstanding; one is enough
    await this.db.outbox.put({
      id,
      kind: 'create',
      surveyId: record.surveyId,
      localRevision: record.localRevision,
      state: 'queued',
      attempts: 0,
      createdAt: isoAt(nowMs),
      nextAttemptAt: nowMs,
    });
  }

  /**
   * Coalescing enqueue for the document pointer.
   *
   * Backoff state is deliberately PRESERVED across re-enqueues. Backoff
   * describes the channel, not the payload: if the server has been refusing for
   * four minutes, the surveyor answering another question is not evidence it has
   * recovered, and resetting the timer on every keystroke would turn a frozen
   * link into a retry storm.
   */
  private async queueDocumentPush(record: StoredSurvey, nowMs: number): Promise<void> {
    // Brief §4: the server is append-only for submitted surveys. Once the seal
    // is known, the device stops offering revisions it cannot accept.
    if (record.sealed === 1) return;

    // A SUBMITTED document has no document push. `PUT /api/surveys/:id` refuses
    // one outright (422 `use_submit_endpoint`) — the submission row carries the
    // whole document through `POST …/submit` instead, so there is nothing here
    // for the office to be missing. Leaving the row queued costs a request that
    // can only ever fail, and a failed request is not free: it sets
    // `SyncStatus.lastError`, which flips the always-visible sync pill to its
    // alarm state for a full backoff — at the exact moment the surveyor is
    // watching to see the job land. Any row queued before the status changed is
    // removed here for the same reason.
    if (isSubmittedStatus(record.status)) {
      await this.db.outbox.delete(outboxId.document(record.surveyId));
      return;
    }

    const id = outboxId.document(record.surveyId);
    const existing = await this.db.outbox.get(id);
    await this.db.outbox.put({
      id,
      kind: 'document',
      surveyId: record.surveyId,
      localRevision: record.localRevision,
      state: existing?.state ?? 'queued',
      attempts: existing?.attempts ?? 0,
      createdAt: existing?.createdAt ?? isoAt(nowMs),
      nextAttemptAt: existing?.nextAttemptAt ?? nowMs,
      ...(existing?.lastError ? { lastError: existing.lastError } : {}),
    });
  }

  private async queuePhotoUpload(
    surveyId: string,
    photoId: string,
    localRevision: number,
    nowMs: number,
  ): Promise<void> {
    const id = outboxId.photo(photoId);
    const existing = await this.db.outbox.get(id);
    if (existing) return; // photo bytes are immutable; one row is the whole job
    await this.db.outbox.put({
      id,
      kind: 'photo',
      surveyId,
      photoId,
      localRevision,
      state: 'queued',
      attempts: 0,
      createdAt: isoAt(nowMs),
      nextAttemptAt: nowMs,
    });
  }

  /**
   * The thumb's own row, behind the photo's (round 8.1, task 3). One per
   * photo, like the photo's: a thumb is immutable once made, so a second
   * capture of the same id would be a duplicate and coalesces onto the row.
   */
  private async queuePhotoThumbUpload(
    surveyId: string,
    photoId: string,
    localRevision: number,
    nowMs: number,
  ): Promise<void> {
    const id = outboxId.photoThumb(photoId);
    const existing = await this.db.outbox.get(id);
    if (existing) return;
    await this.db.outbox.put({
      id,
      kind: 'photo_thumb',
      surveyId,
      photoId,
      localRevision,
      state: 'queued',
      attempts: 0,
      createdAt: isoAt(nowMs),
      nextAttemptAt: nowMs,
    });
  }

  /**
   * Queue the submission. The document itself must already carry the submitted
   * status, gaps and submittedAt — this only says "tell the server".
   */
  async enqueueSubmission(surveyId: string): Promise<OutboxItem> {
    const nowMs = this.now();
    const item = await this.db.transaction('rw', this.db.surveys, this.db.outbox, async () => {
      const record = await this.db.surveys.get(surveyId);
      if (!record) throw new Error(`enqueueSubmission: unknown survey "${surveyId}"`);
      return this.putSubmissionRow(record, nowMs);
    });
    this.emit();
    return item;
  }

  /**
   * The one place a submission row is written. Inside the caller's
   * transaction; idempotent — an existing row keeps its state, attempts and
   * schedule, so a replay never resets a backoff or un-parks anything.
   */
  private async putSubmissionRow(record: StoredSurvey, nowMs: number): Promise<OutboxItem> {
    const id = outboxId.submission(record.surveyId);
    const existing = await this.db.outbox.get(id);
    const next: OutboxItem = {
      id,
      kind: 'submission',
      surveyId: record.surveyId,
      localRevision: record.localRevision,
      state: existing?.state ?? 'queued',
      attempts: existing?.attempts ?? 0,
      createdAt: existing?.createdAt ?? isoAt(nowMs),
      nextAttemptAt: existing?.nextAttemptAt ?? nowMs,
    };
    await this.db.outbox.put(next);
    await this.putSealRow(record.surveyId, record.localRevision, nowMs);
    return next;
  }

  /**
   * The seal notice (D-052 §3), beside every submission row — same
   * transaction, same idempotence: an existing row keeps its schedule. The
   * engine drops it unsent when no photo is owed, because the submission
   * itself then goes in the same pass and says more.
   */
  private async putSealRow(surveyId: string, localRevision: number, nowMs: number): Promise<void> {
    const id = outboxId.seal(surveyId);
    if (await this.db.outbox.get(id)) return;
    await this.db.outbox.put({
      id,
      kind: 'seal',
      surveyId,
      localRevision,
      state: 'queued',
      attempts: 0,
      createdAt: isoAt(nowMs),
      nextAttemptAt: nowMs,
    });
  }

  /**
   * Re-make a promise this phone lost (D-049).
   *
   * A survey that is submitted HERE (`status` submitted) but not sealed by
   * the office (`sealed` 0 — only a submission ack or a sealed pull sets it)
   * owes the office a submission. If no row is queued to carry it, the promise
   * was lost — the pre-D-049 two-transaction gap, or any future way a row can
   * go missing — and nothing else in the app would ever look again. Run at
   * every start, next to `recoverInFlight`; the row is idempotent on the
   * server too, so a survey the office in fact holds settles as
   * `already_submitted` and seals here.
   */
  async recoverLostSubmissions(): Promise<number> {
    const nowMs = this.now();
    const count = await this.db.transaction('rw', this.db.surveys, this.db.outbox, async () => {
      const owed = (await this.db.surveys.toArray()).filter(
        (record) => isSubmittedStatus(record.status) && record.sealed !== 1,
      );
      let made = 0;
      for (const record of owed) {
        if (await this.db.outbox.get(outboxId.submission(record.surveyId))) continue;
        await this.putSubmissionRow(record, nowMs);
        made += 1;
      }
      return made;
    });
    if (count > 0) this.emit();
    return count;
  }

  /**
   * Apply a creation ack: the office has been told this job exists.
   *
   * The flag and the row removal are one transaction, so a crash between them
   * cannot leave a survey that believes it has been announced with a row still
   * promising to announce it, or the reverse.
   */
  async ackCreation(surveyId: string): Promise<StoredSurvey | undefined> {
    const record = await this.db.transaction('rw', this.db.surveys, this.db.outbox, async () => {
      const existing = await this.db.surveys.get(surveyId);
      if (existing) {
        const next: StoredSurvey = { ...existing, creationNotified: 1 };
        await this.db.surveys.put(next);
        await this.db.outbox.delete(outboxId.create(surveyId));
        return next;
      }
      await this.db.outbox.delete(outboxId.create(surveyId));
      return undefined;
    });
    this.emit();
    return record;
  }

  /** Claim a row for one send. */
  async markInFlight(id: string): Promise<void> {
    await this.db.outbox.update(id, { state: 'in_flight' as OutboxState });
  }

  /**
   * Recover rows a killed tab left mid-flight.
   *
   * This is the other half of "mid-sync disconnect must not lose data" (§6.3):
   * the row survived because it is only deleted on ack, and this puts it back in
   * the queue so the next drain actually replays it. Without this, an
   * interrupted send would leave a row that is present but permanently skipped —
   * data not lost, but never delivered either, which is the same thing to Chip.
   */
  async recoverInFlight(): Promise<number> {
    const stuck = await this.db.outbox.where('state').equals('in_flight').toArray();
    if (stuck.length === 0) return 0;
    await this.db.outbox.bulkPut(stuck.map((item) => ({ ...item, state: 'queued' as const })));
    this.emit();
    return stuck.length;
  }

  /**
   * Record a failed attempt: keep the row, bump attempts, push out the retry.
   * A row is NEVER dropped on failure. A photo that will not upload becomes a
   * missing recommended shot (spec §2.4 — warn, never block); a document that
   * will not push stays visible in the sync pill until it does.
   */
  async failOutboxItem(id: string, error: string, nextAttemptAt: number): Promise<void> {
    const existing = await this.db.outbox.get(id);
    if (!existing) return;
    await this.db.outbox.put({
      ...existing,
      state: 'queued',
      attempts: existing.attempts + 1,
      nextAttemptAt,
      lastError: error,
    });
    this.emit();
  }

  /**
   * PUT A ROW BACK WITHOUT COUNTING AN ATTEMPT — "not yet", not "not working".
   *
   * The third outcome of a send, and it is neither of the other two. A photo
   * whose survey the office has no row for yet would be refused 409 by
   * `POST /api/photos`, and that refusal says nothing about the photo: it says
   * the document queued AHEAD of this row has not landed. Recording it through
   * `failOutboxItem` would bump the attempt count, stamp a `lastError` on a row
   * that has nothing wrong with it, and push the retry further out on every
   * pass — so a queue that is merely ordered would look, on screen and in the
   * logs, like a queue that is failing.
   *
   * `attempts` and `lastError` are therefore left exactly as they were. The only
   * thing that changes is when to look again, and the claim being released.
   */
  async deferOutboxItem(id: string, nextAttemptAt: number): Promise<void> {
    const existing = await this.db.outbox.get(id);
    if (!existing) return;
    await this.db.outbox.put({ ...existing, state: 'queued', nextAttemptAt });
    this.emit();
  }

  /**
   * PARK a row the server refused on its own merits — keep it, stop sending it.
   *
   * The counterpart of `failOutboxItem`, and the difference is the whole point:
   * `fail` means "not this time", `park` means "not these bytes, ever". A parked
   * row is skipped by `dueOutbox()` (which returns only `queued`) and survives a
   * reload, so the retry loop actually stops instead of restarting on the next
   * `visibilitychange`.
   *
   * NOTHING IS DISCARDED. The row, the document's PhotoRef and the photo bytes
   * are all untouched — a surveyor's work is never destroyed to tidy up a queue.
   * The row leaves this state only when a person decides: `retryOutboxItem`, or
   * `deletePhoto` if they would rather take the picture again.
   */
  async parkOutboxItem(id: string, error: string): Promise<void> {
    const existing = await this.db.outbox.get(id);
    if (!existing) return;
    await this.db.outbox.put({
      ...existing,
      state: 'blocked',
      attempts: existing.attempts + 1,
      lastError: error,
    });
    this.emit();
  }

  /**
   * Un-park a row because the surveyor asked for it. Attempts reset to zero:
   * this is a deliberate act by a person, not the automatic loop, so it starts
   * from the front of the backoff schedule and is sent on the very next drain.
   */
  async retryOutboxItem(id: string, nowMs: number = this.now()): Promise<boolean> {
    const existing = await this.db.outbox.get(id);
    if (!existing || existing.state !== 'blocked') return false;
    await this.db.outbox.put({
      ...existing,
      state: 'queued',
      attempts: 0,
      nextAttemptAt: nowMs,
    });
    this.emit();
    return true;
  }

  /**
   * Apply a document ack.
   *
   * The `sentRevision` guard is the interesting part. The drain sends the
   * document as it stood at revision N; the surveyor keeps working while the
   * request is in flight. If the record has moved on by the time the ack lands,
   * deleting the row would strand every answer written during the round trip.
   * So: record the server's position either way, but only clear the row when
   * what was acked is still what the device holds.
   */
  async ackDocumentPush(
    surveyId: string,
    sentRevision: number,
    serverRevision: number,
    options: { sealed?: boolean } = {},
  ): Promise<StoredSurvey | undefined> {
    const nowMs = this.now();
    const record = await this.db.transaction('rw', this.db.surveys, this.db.outbox, async () => {
      const existing = await this.db.surveys.get(surveyId);
      if (!existing) return undefined;

      const settled = existing.localRevision === sentRevision;
      const syncedRevision = Math.max(existing.syncedRevision, sentRevision);
      const next: StoredSurvey = {
        ...existing,
        syncedRevision,
        serverRevision: Math.max(existing.serverRevision, serverRevision),
        dirty: existing.localRevision > syncedRevision ? 1 : 0,
        sealed: options.sealed ? 1 : existing.sealed,
        lastSyncedAt: isoAt(nowMs),
      };
      await this.db.surveys.put(next);

      const id = outboxId.document(surveyId);
      if (settled || next.sealed === 1) {
        await this.db.outbox.delete(id);
      } else {
        // Newer content arrived mid-flight: re-point the row at it and let the
        // next pass carry it. Attempts reset — this payload has never failed.
        const item = await this.db.outbox.get(id);
        if (item) {
          await this.db.outbox.put({
            ...item,
            localRevision: next.localRevision,
            state: 'queued',
            attempts: 0,
            nextAttemptAt: nowMs,
          });
        }
      }
      return next;
    });
    this.emit();
    return record;
  }

  /**
   * Apply a photo ack: bytes are in R2, the row's job is done. State change and
   * row removal are one transaction so a crash between them is not a state where
   * a photo reads as uploaded but is still queued.
   */
  async ackPhotoUpload(surveyId: string, photoId: string): Promise<void> {
    await this.db.transaction('rw', this.db.surveys, this.db.outbox, async () => {
      const existing = await this.db.surveys.get(surveyId);
      if (existing) {
        const photo = existing.document.photos.find((p) => p.id === photoId);
        if (photo) {
          photo.uploadState = 'uploaded';
          await this.db.surveys.put(existing);
        }
      }
      await this.db.outbox.delete(outboxId.photo(photoId));
    });
    this.emit();
  }

  /**
   * Apply a thumb ack: the office holds the small picture, the row's job is
   * done. Nothing on the document changes — `PhotoRef` knows nothing of
   * thumbs, on purpose — so this is the row's deletion and no more.
   */
  async ackThumbUpload(photoId: string): Promise<void> {
    await this.db.outbox.delete(outboxId.photoThumb(photoId));
    this.emit();
  }

  /**
   * Remove a row whose work no longer exists or can no longer be done — the
   * survey or photo it points at was deleted underneath it, or the document it
   * points at has been submitted and `PUT /api/surveys/:id` would refuse it.
   *
   * This is the only removal path that is not an ack, and it stays deliberately
   * narrow: a row is never dropped because it FAILED, only because there is no
   * longer a call it could make. The two are easy to confuse and only one of
   * them is safe.
   */
  async dropOutboxItem(id: string): Promise<void> {
    await this.db.outbox.delete(id);
    this.emit();
  }

  /** Apply a submission ack: the record is sealed, on the server and here. */
  async ackSubmission(surveyId: string, serverRevision: number): Promise<StoredSurvey | undefined> {
    const nowMs = this.now();
    const record = await this.db.transaction('rw', this.db.surveys, this.db.outbox, async () => {
      const existing = await this.db.surveys.get(surveyId);
      if (!existing) return undefined;
      const next: StoredSurvey = {
        ...existing,
        serverRevision: Math.max(existing.serverRevision, serverRevision),
        syncedRevision: existing.localRevision,
        dirty: 0,
        sealed: 1,
        lastSyncedAt: isoAt(nowMs),
      };
      await this.db.surveys.put(next);
      await this.db.outbox.delete(outboxId.submission(surveyId));
      // The office holds the seal itself now; a notice that it was coming is
      // moot (D-052 §3).
      await this.db.outbox.delete(outboxId.seal(surveyId));
      // Sealed means no further revisions will ever be accepted, so a pending
      // document push is dead weight. Photo rows stay: R2 still wants the bytes.
      // The CREATION row stays too, and that is the whole point of it being its
      // own row — a survey created and submitted with the radio off arrives here
      // having never announced itself, and deleting the notice now is exactly
      // how spec §2.1 used to fail silently.
      await this.db.outbox.delete(outboxId.document(surveyId));
      return next;
    });
    this.emit();
    return record;
  }

  /**
   * The server refused a push because the survey is already submitted. Nothing
   * local is destroyed — the document stays exactly as the surveyor left it, and
   * the seal only stops the device offering revisions the server cannot take.
   */
  async markSealed(surveyId: string, serverRevision: number): Promise<StoredSurvey | undefined> {
    const record = await this.db.transaction('rw', this.db.surveys, this.db.outbox, async () => {
      const existing = await this.db.surveys.get(surveyId);
      if (!existing) return undefined;
      const next: StoredSurvey = {
        ...existing,
        sealed: 1,
        serverRevision: Math.max(existing.serverRevision, serverRevision),
      };
      await this.db.surveys.put(next);
      await this.db.outbox.delete(outboxId.document(surveyId));
      await this.db.outbox.delete(outboxId.submission(surveyId));
      // The creation notice is untouched: a seal says the server holds this
      // survey's record, not that anyone was told the job exists.
      return next;
    });
    this.emit();
    return record;
  }
}
